import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import React from 'react';

/**
 * CaptionedImage - a Tiptap node that renders `<figure><img><figcaption></figure>`
 * with an inline caption input directly under the image. Click the image to
 * focus the caption field.
 *
 * Also parses existing legacy-imported <figure> markup so edits round-trip
 * cleanly.
 */

const CaptionedImageComponent = ({ node, updateAttributes, selected }) => {
  const { src, alt, caption } = node.attrs;
  const editCaption = React.useCallback(() => {
    // Use a native prompt for maximum mobile-browser compatibility. Inline
    // inputs inside ProseMirror node views intermittently refuse focus on
    // touch devices (iOS Safari + Chrome mobile) which is why we bypass it.
    const next = window.prompt(
      caption ? 'Edit image caption:' : 'Add image caption (leave blank for none):',
      caption || ''
    );
    if (next === null) return; // user cancelled
    updateAttributes({ caption: next.trim() });
  }, [caption, updateAttributes]);

  return (
    <NodeViewWrapper
      as="figure"
      className="captioned-image-node"
      data-selected={selected ? 'true' : 'false'}
      contentEditable={false}
    >
      <button
        type="button"
        className="cap-image-btn"
        onClick={editCaption}
        aria-label={caption ? 'Edit image caption' : 'Add image caption'}
      >
        <img src={src} alt={alt || caption || ''} draggable={false} />
        <span className="cap-image-hint" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
          {caption ? 'Tap image to edit caption' : 'Tap image to add caption'}
        </span>
      </button>
      <button
        type="button"
        className={caption ? 'cap-text has-caption' : 'cap-text empty'}
        onClick={editCaption}
        aria-label={caption ? 'Edit caption' : 'Add caption'}
      >
        {caption || 'Add a caption'}
      </button>
    </NodeViewWrapper>
  );
};

export const CaptionedImage = Node.create({
  name: 'captionedImage',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  // Higher than the default (100) so this node claims <img> and <figure> tags
  // before the plain Image extension can grab them.
  priority: 1000,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
      caption: { default: '' },
    };
  },

  parseHTML() {
    return [
      // Any <figure> that contains an <img> - matches the legacy importer's
      // `.captioned-image-container` markup AND editor-authored figures.
      // Preserves the <figcaption> text so imports round-trip cleanly.
      {
        tag: 'figure',
        getAttrs: (el) => {
          const img = el.querySelector('img');
          if (!img) return false;
          const cap = el.querySelector('figcaption');
          return {
            src: img.getAttribute('src') || null,
            alt: img.getAttribute('alt') || '',
            caption: (cap?.textContent || '').trim(),
          };
        },
      },
      // Bare <img> - fallback for imports that never used a <figure> wrapper.
      {
        tag: 'img[src]',
        getAttrs: (el) => ({
          src: el.getAttribute('src'),
          alt: el.getAttribute('alt') || '',
          caption: '',
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, alt, caption } = HTMLAttributes;
    if (!src) return ['span', {}];
    if (caption && String(caption).trim()) {
      return [
        'figure',
        mergeAttributes({ class: 'captioned-image-node' }),
        ['img', { src, alt: alt || '' }],
        ['figcaption', {}, caption],
      ];
    }
    return ['img', { src, alt: alt || '' }];
  },

  addCommands() {
    return {
      setCaptionedImage:
        (options) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: options,
          }),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(CaptionedImageComponent);
  },
});

export const CAPTIONED_IMAGE_CSS = `
.captioned-image-node {
  margin: 1.5rem 0 1.75rem;
  padding: 0;
  display: block;
  border-radius: 0.75rem;
  border: 2px solid transparent;
  transition: border-color 120ms ease;
}
.captioned-image-node[data-selected="true"] { border-color: rgba(220,38,38,0.55); }

/* Image wrapper is a real button so mobile browsers reliably fire tap events */
.captioned-image-node .cap-image-btn {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  border-radius: 0.5rem;
  overflow: hidden;
  -webkit-tap-highlight-color: rgba(220,38,38,0.25);
}
.captioned-image-node .cap-image-btn img {
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto;
  border-radius: 0.5rem;
  border: 1px solid rgb(39,39,42);
  pointer-events: none;
}
.captioned-image-node .cap-image-hint {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: rgba(0,0,0,0.75);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: none;
  pointer-events: none;
  opacity: 0;
  transition: opacity 150ms ease;
  border: 1px solid rgba(220,38,38,0.5);
}
.captioned-image-node .cap-image-btn:hover .cap-image-hint,
.captioned-image-node[data-selected="true"] .cap-image-hint { opacity: 1; }
/* On touch devices there's no hover, so keep the hint visible */
@media (hover: none) {
  .captioned-image-node .cap-image-hint { opacity: 1; }
}

/* Caption line - always a tappable button so users don't need to hit the tiny
   line to edit; the whole width is clickable */
.captioned-image-node .cap-text {
  display: block;
  width: 90%;
  margin: 0.6rem auto 0;
  padding: 0.45rem 0.75rem;
  background: transparent;
  border: 0;
  border-top: 1px dashed rgba(220,38,38,0.45);
  text-align: center;
  font-style: italic;
  font-size: 0.85rem;
  line-height: 1.4;
  color: #a1a1aa;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(220,38,38,0.2);
  font-family: inherit;
}
.captioned-image-node .cap-text.empty {
  color: #71717a;
  font-style: italic;
  opacity: 0.75;
}
.captioned-image-node .cap-text.empty::before {
  content: '+ ';
  font-style: normal;
  color: #ef4444;
  font-weight: 700;
  margin-right: 0.2rem;
}
.captioned-image-node .cap-text.has-caption { color: #d4d4d8; }
.captioned-image-node .cap-text:hover,
.captioned-image-node .cap-text:focus-visible {
  color: #fff;
  border-top-color: rgba(220,38,38,0.85);
  background: rgba(220,38,38,0.05);
  outline: none;
}
`;

export default CaptionedImage;
